import { useMemo, type Ref } from 'react'
import { Drawer } from 'vaul'
import { ActionSheetFrame } from './ActionSheetFrame'
import { ActionSheetPanel } from './ActionSheetPanel'
import type { ActionSheetOptions } from './action-sheet-store.ts'
import {
  closeActionSheet,
  updateActionSheet,
} from './action-sheet-store.ts'

type ActionSheetDrawerProps = {
  open: boolean
  options: ActionSheetOptions | null
  closeRef: Ref<HTMLButtonElement>
  container: HTMLElement | null
  onOpenChange: (open: boolean, id: number) => void
  onAnimationEnd: (open: boolean, id: number) => void
  onActionClose?: () => void
}

function resolveContent(options: ActionSheetOptions) {
  if (options.render) {
    return options.render({
      close: closeActionSheet,
      update: updateActionSheet,
    })
  }
  return options.content ?? null
}

export function ActionSheetDrawer({
  open,
  options,
  container,
  closeRef,
  onOpenChange,
  onAnimationEnd,
  onActionClose,
}: ActionSheetDrawerProps) {
  const content = useMemo(() => {
    if (!options) {
      return null
    }
    return resolveContent(options)
  }, [options])

  if (!options) {
    return null
  }

  const dismissible = options.dismissible ?? true

  return (
    <ActionSheetFrame.Provider value={{ container, depth: 0 }}>
      <Drawer.Root
        key={options.id}
        open={open}
        onOpenChange={nextOpen => onOpenChange(nextOpen, options.id)}
        onAnimationEnd={nextOpen => onAnimationEnd(nextOpen, options.id)}
        dismissible={dismissible}
        container={container}
        shouldScaleBackground={false}
        repositionInputs={false}
        noBodyStyles
      >
        <Drawer.Close asChild><button ref={closeRef} type="button" hidden aria-hidden="true" tabIndex={-1} /></Drawer.Close>
        <ActionSheetPanel options={options} content={content} onActionClose={onActionClose} />
      </Drawer.Root>
    </ActionSheetFrame.Provider>
  )
}
